import { useState } from 'react'
import { toPanelError } from '@/console/api/errors'
import { HoldRuleView } from '@/console/api/types'
import { CreateHoldRequest, createHoldRule, deleteHoldRule, recheckHoldDomain, useHeldWorkflows, useHoldRules } from '@/console/data/holds'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Note } from '@/console/ui/Note'
import { PageHeading } from '@/console/ui/PageHeading'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { TABS } from '@/console/shell/tabs'
import { HoldCreateForm } from './HoldCreateForm'
import { HeldWorkflowList } from './HeldWorkflowList'
import { HoldRuleRow, toHeldWorkflowRows, toHoldRuleRows } from './holdRows'
import { HoldRuleTable } from './HoldRuleTable'
import { HoldFormInput, parseHoldValues } from './holdForm'

const tab = TABS.find((t) => t.key === 'disp') ?? TABS[0]

type ActionError = { kind: 'forbidden' | 'other'; message: string }

/**
 * `HoldFormInput` → `CreateHoldRequest`(요청 DTO). `holdForm.ts` 는 순수 모듈이라 수정 금지 — 그 안의
 * `buildHoldRuleFromForm` 은 `HoldRuleView`(응답 DTO 모양)를 만들 뿐이고, `createHoldRule` 이 요구하는
 * 요청 DTO(`MaintenanceHoldRequest`·`AttributeHoldRequest`·`PredicateHoldRequest`)와는 이름이 겹치는
 * 필드조차 필수·옵셔널이 달라 그대로 못 쓴다(`data/holds.ts` 상단 주석 참고). 이 변환은 순수 모듈이
 * 아닌 이 화면 파일에 둔다. `parseHoldValues` 는 `holdForm.ts` 의 export 를 그대로 재사용한다(수정
 * 없이 import 만).
 */
const buildCreateHoldRequest = (input: HoldFormInput): CreateHoldRequest => {
  const holdId = input.holdId.trim()

  if (input.kind === 'maintenance') {
    return {
      kind: 'maintenance',
      body: { holdId, domain: input.domain, from: input.from ?? Date.now(), toExclusive: input.until ?? Date.now() },
    }
  }

  const values = parseHoldValues(input.values)

  if (input.kind === 'predicate') {
    return { kind: 'predicate', body: { holdId, domain: input.domain, predicate: { op: 'leaf', field: input.field, values } } }
  }

  return { kind: 'attr', body: { holdId, domain: input.domain, field: input.field, values } }
}

/**
 * LOGISPRD-12506 최종 리뷰 C1 — Hold 해제·규칙 추가·전부 깨우기가 로컬 state 만 바꾸고 서버에
 * 아무것도 보내지 않던 문제를 고친다. 이제 세 조치 전부 `data/holds.ts` 의 실제 함수(`deleteHoldRule`·
 * `createHoldRule`·`recheckHoldDomain`)를 부른다. 그 함수들은 성공하면 내부에서 `consoleQueryClient`
 * (이 앱의 단일 QueryClient, `index.tsx`)의 같은 쿼리 키로 `fetchQuery` 를 다시 걸어 캐시를 갱신한다 —
 * 이 컴포넌트의 `useHoldRules`/`useHeldWorkflows` 는 같은 캐시를 구독 중이라 그 갱신이 그대로
 * 화면에 반영된다(별도로 `refetch()` 를 다시 부를 필요가 없다). 로컬 add/remove 오버레이
 * (`addedRules`·`removedHoldIds`)는 그래서 완전히 걷어냈다 — 서버 응답(또는 read-back)이 유일한
 * 진실의 원천이다. 실패하면 조용히 넘어가지 않고 `Callout` 으로 사유를 보여준다.
 */
export const DispatcherTab = (): JSX.Element => {
  const { state: holdRulesState, refetch: refetchHoldRules } = useHoldRules()
  const { state: heldWorkflowsState, hasMore: heldWorkflowsHasMore, refetch: refetchHeldWorkflows } = useHeldWorkflows()

  const [pendingDelete, setPendingDelete] = useState<HoldRuleRow | null>(null)
  const [deleteError, setDeleteError] = useState<ActionError | null>(null)
  const [createError, setCreateError] = useState<ActionError | null>(null)
  const [createNotice, setCreateNotice] = useState<string | null>(null)
  const [wakeAllError, setWakeAllError] = useState<ActionError | null>(null)

  /**
   * PR 리뷰 Critical — 세 조치(해제·등록·전부 깨우기)가 fire-and-forget 이라 응답 전에 같은
   * 버튼을 다시 누를 수 있었다. 서버에 dedupe 가 없어(핸드오프 §4.1) 같은 holdId 가 중복
   * 등록되거나 이미 지운 규칙을 또 지우는 요청이 나간다. 하나의 `busy` 로 세 조치를 묶는다 —
   * 어차피 셋 다 같은 Hold 목록을 건드리므로 동시에 돌 이유가 없고, 상태 3개를 따로 두면
   * "삭제 중에 등록" 같은 조합을 매번 따져야 한다.
   */
  const [busy, setBusy] = useState<'delete' | 'create' | 'wake' | null>(null)

  /**
   * 등록 성공 시 폼을 초기화한다(PR 리뷰 Warning — 같은 값이 남아 한 번 더 누르면 같은 규칙을
   * 다시 제출한다). `HoldCreateForm` 은 리뷰를 거친 자산이라 reset API 를 새로 뚫는 대신 key 를
   * 바꿔 다시 마운트시킨다.
   */
  const [createFormKey, setCreateFormKey] = useState(0)

  const now = Date.now()
  const holdRuleRows = holdRulesState.status === 'ready' ? toHoldRuleRows(holdRulesState.data, now) : []
  const heldWorkflowRows = heldWorkflowsState.status === 'ready' ? toHeldWorkflowRows(heldWorkflowsState.data, now) : []

  const requestDelete = (row: HoldRuleRow): void => setPendingDelete(row)

  const confirmDelete = (): void => {
    const target = pendingDelete
    setPendingDelete(null)

    if (!target) {
      return
    }

    // 버튼은 `busy` 로 잠겨 있지만, 잠기기 전에 열어 둔 다이얼로그에서 확인하면 여기 도달한다.
    // 그때 조용히 return 하면 다이얼로그만 닫히고 아무 일도 일어나지 않아 "해제됐다" 로 오인한다.
    if (busy) {
      setDeleteError({ kind: 'other', message: '다른 조치가 진행 중이라 실행하지 않았어요. 끝난 뒤 다시 눌러주세요.' })
      return
    }

    // 표를 만든 원본 목록에서 같은 holdId 의 raw HoldRuleView 를 찾는다 — `deleteHoldRule` 은
    // 계산된 행(`HoldRuleRow`)이 아니라 원본 규칙을 받는다(삭제 전 스냅샷을 남기기 위해서다).
    const rawRule: HoldRuleView | undefined =
      holdRulesState.status === 'ready' ? holdRulesState.data.find((r) => r.holdId === target.holdId) : undefined

    if (!rawRule) {
      return
    }

    setDeleteError(null)
    setBusy('delete')
    deleteHoldRule(rawRule)
      .catch((err) => setDeleteError(toPanelError(err)))
      .finally(() => setBusy(null))
  }

  /**
   * PR 리뷰 High — read-back 결과를 실제로 확인한다.
   *
   * 핸드오프 §4.1 의 함정은 "등록 200 ≠ 저장 성공" 이고, 그래서 등록 뒤 `GET /` 로 목록을 다시
   * 읽는다(`confirmRegistration` 체크박스). 예전에는 그 결과(`confirmed`)를 받아놓고 버려서,
   * 응답만 200 이고 실제로 저장되지 않은 경우에 화면이 아무 말도 하지 않았다 — 온콜 담당자는
   * Hold 가 걸렸다고 믿고 넘어간다. read-back 을 하는 이유가 바로 그 경우를 잡기 위해서다.
   *
   * 체크를 끈 경우(`confirmed === null`)에는 목록 재조회가 없어 표도 그대로다. 그때도 등록이
   * 됐다는 사실 자체는 보여야 하므로 `created.holdId` 로 성공 문구를 낸다.
   */
  const handleCreate = (input: HoldFormInput, confirmRegistration: boolean): void => {
    if (busy) {
      // 이전 등록의 성공 문구를 반드시 먼저 지운다 — 남겨 두면 이번 등록이 나가지 않았는데도
      // "등록됨" 이 그대로 보여 온콜 담당자가 도메인을 멈췄다고 믿는다(PR 리뷰).
      setCreateNotice(null)
      setCreateError({ kind: 'other', message: '다른 조치가 진행 중이라 실행하지 않았어요. 끝난 뒤 다시 눌러주세요.' })
      return
    }

    setCreateError(null)
    setCreateNotice(null)
    setBusy('create')
    // 삭제는 propagate=true 고정(`api/holds.ts`)과 같은 판단으로, 등록도 최대 1시간 지연 없이 바로
    // 반영되도록 propagate=true 로 보낸다.
    createHoldRule(buildCreateHoldRequest(input), { propagate: true, confirmRegistration })
      .then(({ created, confirmed }) => {
        if (confirmed && !confirmed.some((rule) => rule.holdId === created.holdId)) {
          setCreateError({
            kind: 'other',
            message: `등록 응답은 200 인데 목록에 ${created.holdId} 가 없어요. 저장되지 않았을 수 있어요 — 다시 조회해 확인하세요.`,
          })
          return
        }

        setCreateNotice(
          confirmed
            ? `${created.holdId} 등록됨 — 목록 재조회로 반영을 확인했어요.`
            : `${created.holdId} 등록됨 — 반영 확인(목록 재조회)은 하지 않았어요.`,
        )
        setCreateFormKey((key) => key + 1)
      })
      .catch((err) => setCreateError(toPanelError(err)))
      .finally(() => setBusy(null))
  }

  const handleWakeAll = (): void => {
    if (busy) {
      setWakeAllError({ kind: 'other', message: '다른 조치가 진행 중이라 실행하지 않았어요. 끝난 뒤 다시 눌러주세요.' })
      return
    }

    setWakeAllError(null)
    setBusy('wake')
    // "전부 깨우기" 는 화면에 도메인 선택지가 없다 — 버튼 문구 그대로 전체 도메인('*')을 재평가한다.
    recheckHoldDomain('*')
      .catch((err) => setWakeAllError(toPanelError(err)))
      .finally(() => setBusy(null))
  }

  return (
    <>
      <PageHeading title={tab.heading} sub={tab.sub} />

      <SectionHeading
        id="s-disp-holds"
        level={2}
        title="Hold 관리"
        badge={<SourceBadge tone="have">기존 · GET /workflow/holds</SourceBadge>}
        right={
          <Button variant="ghost" size="sm" onClick={refetchHoldRules}>
            새로고침
          </Button>
        }
      />
      <ResultState state={holdRulesState}>
        <HoldRuleTable rows={holdRuleRows} onRequestDelete={requestDelete} disabled={busy !== null} />
      </ResultState>
      {deleteError ? <Callout tone="crit">{deleteError.message}</Callout> : null}
      <Note>해제 = DELETE /holds/{'{id}'}?propagate=true. 자동 생성 규칙은 지워도 다음 스케줄에 되살아나요.</Note>

      <SectionHeading id="s-disp-create" level={2} title="규칙 추가" badge={<SourceBadge tone="have">기존 · POST /holds/{'{type}'}</SourceBadge>} />
      <HoldCreateForm key={createFormKey} onCreate={handleCreate} submitting={busy !== null} />
      {createError ? <Callout tone="crit">{createError.message}</Callout> : null}
      {createNotice ? <Callout tone="good">{createNotice}</Callout> : null}

      <SectionHeading
        id="s-disp-held"
        level={2}
        title="멈춘 워크플로"
        badge={<SourceBadge tone="have">기존 · GET /holds/subscribers</SourceBadge>}
        right={
          <Button variant="ghost" size="sm" onClick={refetchHeldWorkflows}>
            새로고침
          </Button>
        }
      />
      <ResultState state={heldWorkflowsState}>
        <HeldWorkflowList rows={heldWorkflowRows} onWakeAll={handleWakeAll} hasMore={heldWorkflowsHasMore} disabled={busy !== null} />
      </ResultState>
      {wakeAllError ? <Callout tone="crit">{wakeAllError.message}</Callout> : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Hold 규칙을 해제할까요?"
        body={
          pendingDelete ? (
            <>
              <div>
                holdId: <b>{pendingDelete.holdId}</b>
              </div>
              <div>적용 대상: {pendingDelete.target}</div>
              <p>propagate=true 로 보냅니다 — 그래야 최대 1시간 지연 없이 풀립니다.</p>
            </>
          ) : null
        }
        confirmLabel="해제"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
